/**
 * How one hour's score is made, as a small table: one row per range — its
 * period, what its formula gave (each metric's weight → contribution for
 * sliders, the expression for blocks), the range's result, its share and the
 * points it adds — and the total when several ranges add up. "No data in
 * this range" when the tracker had no row for that hour there.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import {
  type DropGenHourFormula,
  type DropGenHourScoreBreakdownRow,
  type DropGenHourScoreContribution,
  expandSubrefsWithWeights,
  HOUR_METRIC_LABELS,
  hourRangeLabel,
} from '@trafficpulse/shared';

import { scoreLabel } from './hour-chart-math';

const TH = 'px-2 py-1 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground';
const TD = 'px-2 py-1 align-top';

export const HourScoreBreakdown: FC<{ rows: DropGenHourScoreBreakdownRow[]; formula?: DropGenHourFormula }> = ({ rows, formula }) => {
  const several = rows.length > 1;
  const total = rows.reduce((s, r) => s + r.weightedScore, 0);
  return (
    <table className="w-full text-xs" aria-label="Score breakdown">
      <thead>
        <tr className="border-b border-border/60">
          <th className={TH}>Range</th>
          <th className={`${TH} hidden sm:table-cell`}>Formula</th>
          <th className={`${TH} text-right`}>Result</th>
          {several ? <th className={`${TH} text-right`}>Share</th> : null}
          {several ? <th className={`${TH} text-right`}>Points</th> : null}
        </tr>
      </thead>
      <tbody className="font-mono tabular-nums">
        {rows.map((r) => (
          <tr key={r.groupId} className="border-b border-border/40 last:border-0">
            <td className={`${TD} font-sans`}>
              <span className="font-medium">{r.groupName}</span>
              <span className="block text-[11px] text-muted-foreground">{hourRangeLabel(r.rangeKey)}</span>
              <span className="mt-0.5 block break-words font-mono text-[11px] text-muted-foreground sm:hidden">
                {r.noData ? 'no data in this range' : contributionLine(r, formula)}
              </span>
            </td>
            <td className={`${TD} hidden break-words text-muted-foreground sm:table-cell`}>{r.noData ? <span className="font-sans italic">no data in this range</span> : contributionLine(r, formula)}</td>
            <td className={`${TD} text-right`}>{r.noData ? '—' : scoreLabel(r.rawScore)}</td>
            {several ? <td className={`${TD} text-right text-muted-foreground`}>{Math.round(r.groupWeight)}%</td> : null}
            {several ? <td className={`${TD} text-right font-semibold`}>{scoreLabel(r.weightedScore)}</td> : null}
          </tr>
        ))}
        {several ? (
          <tr>
            <td className={`${TD} font-sans text-muted-foreground`} colSpan={3}>Score</td>
            <td className={`${TD} text-right font-semibold`}>{scoreLabel(total)}</td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );
};

const pct = (w: number) => `${Math.round(w * 100) / 100}%`;

/** "CR 2.10", or a "Lowest" row's distance below the top: "↓ CPC (0.80 − 0.42)". */
function partLabel(c: DropGenHourScoreContribution): string {
  const name = HOUR_METRIC_LABELS[c.metric] ?? c.metric;
  if (!c.lowest) return `${name} ${scoreLabel(c.value)}`;
  return c.max === undefined ? `↓ ${name} −${scoreLabel(c.value)}` : `↓ ${name} (${scoreLabel(c.max)} − ${scoreLabel(c.value)})`;
}

/**
 * The range's math, every number shown, adding up to its result:
 * sliders "CR 20.10 × 60% = 12.06 + ROI 158.60 × 40% = 63.44";
 * blocks each sub-formula "Conv (cr + cv) 24.30 × 70% = 17.01 + …";
 * otherwise the formula that ran, sub-formulas expanded with their weights.
 */
export function contributionLine(r: DropGenHourScoreBreakdownRow, formula?: DropGenHourFormula): string {
  if (r.contributions && r.contributions.length > 0)
    return r.contributions
      .map((c) => `${partLabel(c)} × ${pct(c.weight)} = ${scoreLabel(c.contribution)}`)
      .join(' + ');
  const subs = formula?.groups.find((g) => g.id === r.groupId)?.blocks?.subformulas ?? [];
  const parts = r.subformulaContributions ?? [];
  const master = expandSubrefsWithWeights(r.expression, subs);
  if (parts.length === 0) return master;
  const line = parts
    .map((p) => `${p.name} (${p.expression || '0'}) ${scoreLabel(p.value)}${p.weight != null ? ` × ${pct(p.weight)} = ${scoreLabel(p.contribution)}` : ''}`)
    .join(' + ');
  // When the master does more than add the sub-formulas up, say what it does.
  const plainSum = /^\s*__sub_\w+(\s*\+\s*__sub_\w+)*\s*$/.test(r.expression);
  return plainSum ? line : `${line} → ${master}`;
}
